import Button from "devextreme-react/button";
import SelectBox from "devextreme-react/select-box";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createDefaultTask, getDefaultModules } from "../../api/v2/TasklistsApi";
import { detectOutOfSync } from "../../api/v2/TemplateSyncApi";
import { DefaultModuleDto } from "../../dtos/DefaultModuleDto";
import { DefaultPhaseDto } from "../../dtos/DefaultPhaseDto";
import { GrantDto } from "../../dtos/GrantDto";
import AddDefaultTaskDialog from "../shared/tasklist/tasks/AddDefaultTaskDialog";
import TemplateTasksMain from "./TemplateTasksMain";
import DcStatusSyncButton from "./DcStatusSyncButton";
import { Modules } from "../../dtos/Modules";
import { useAppContext } from "../shared/AppContext";

type Option = { id: number; name: string };

export default function TasklistTemplates() {
    const app = useAppContext();
    const { moduleId: moduleIdParam } = useParams<{ moduleId?: string }>();
    const navigate = useNavigate();

    const [options, setOptions] = useState<Option[]>([]);
    const [modules, setModules] = useState<DefaultModuleDto[]>([]);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [outOfSyncCount, setOutOfSyncCount] = useState<number>(0);

    // Parse moduleId from URL param
    const selectedModuleId = moduleIdParam ? parseInt(moduleIdParam, 10) : null;
    const selectedModule = modules.find(m => m.id === selectedModuleId);
    const allowAddTask = selectedModule?.settings?.allowAddTask ?? true;

    useEffect(() => {
        (async () => {
            const results = await getDefaultModules(true);
            const data: DefaultModuleDto[] | null = results?.data ?? null;
            if (results?.error) return;
            if (data) {
                setModules(data);
                setOptions(data.map(m => ({ id: m.id, name: m.name })));
            }
        })();
    }, []);

    // Refresh sync status. API requires SU role; non-SU users get an error which we silently ignore
    // (count stays at 0, badge stays hidden).
    const refreshSyncStatus = useCallback(async () => {
        if (!selectedModuleId) {
            setOutOfSyncCount(0);
            return;
        }
        const res = await detectOutOfSync(selectedModuleId);
        if (res?.error) {
            setOutOfSyncCount(0);
            return;
        }
        setOutOfSyncCount(res?.data?.length ?? 0);
    }, [selectedModuleId]);

    // Auto-detect on module change
    useEffect(() => {
        refreshSyncStatus();
    }, [refreshSyncStatus]);

    // Navigate to module URL when selection changes
    const handleModuleChange = (moduleId: number | null) => {
        if (moduleId) {
            navigate(`/tasklist-templates/modules/${moduleId}`);
        } else {
            navigate('/tasklist-templates');
        }
    };

    // minimal GrantDto for the shared UI
    const fakeGrant = useMemo(
        () =>
        ({
            id: "TEMPLATE-GRANT",
            grantNumber: "TEMPLATE",
        } as unknown as GrantDto),
        []
    );

    const handleSaveTask = async (task: DefaultPhaseDto) => {

        if (!selectedModuleId) return;

        await createDefaultTask(selectedModuleId, task);

        setRefreshKey(prev => prev + 1);
        setShowAddDialog(false);
        refreshSyncStatus();
    };

    const showSyncBadge = outOfSyncCount > 0;
    const syncButtonText = showSyncBadge ? `Sync Status (${outOfSyncCount})` : "Sync Status";

    if (!app.UserIsSuper) {
        return (
            <div className="container-fluid" style={{ padding: '20px' }}>
                <h4>Access Denied</h4>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }

    return (
        // minmax(0, 1fr) rather than the implicit auto: an auto grid column sizes to its content, so the
        // task list below grew to whatever width its tab strip wanted instead of the strip having to
        // fit. With the column capped, the strip overflows and its nav arrows take over.
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <SelectBox
                    dataSource={options}
                    valueExpr="id"
                    displayExpr="name"
                    value={selectedModuleId}
                    onValueChanged={(e) => handleModuleChange(e.value)}
                    placeholder="Select a module"
                    searchEnabled
                    width={400}
                />

                <Button
                    text="Add Default Task"
                    type="default"
                    stylingMode="contained"
                    onClick={() => setShowAddDialog(true)}
                    disabled={!selectedModuleId || !allowAddTask}
                />

                <Button
                    text={syncButtonText}
                    type={showSyncBadge ? "danger" : "normal"}
                    stylingMode={showSyncBadge ? "contained" : "outlined"}
                    onClick={() => navigate(`/template-sync${selectedModuleId ? `?module=${selectedModuleId}` : ''}`)}
                    disabled={!selectedModuleId}
                />

                {selectedModuleId === Modules.DC && app.configuration?.dcStatusSync && <DcStatusSyncButton />}
            </div>

            <AddDefaultTaskDialog
                visible={showAddDialog}
                onClose={() => setShowAddDialog(false)}
                onSave={handleSaveTask}
            />

            {selectedModuleId != null && (
                <TemplateTasksMain
                    key={`${selectedModuleId}-${refreshKey}`}
                    defaultModuleId={selectedModuleId}
                    grantData={fakeGrant}
                    deepLinkHash={null}
                    onTemplateChanged={refreshSyncStatus}
                />
            )}
        </div>
    );
}
